import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import AuthService from "../../../api/services/AuthService";
import { pairMatchesContractFilter, settlementMatchesContractCode } from "./orderFilterHelpers";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";
import {
  extractFuturesOrders,
  mapFuturesOrderHistoryRow,
  matchesFuturesOrderType,
  toFuturesApiSymbol,
} from "./futuresProfileOrderHelpers";

/** `order_type` values returned by GET /v1/futures/orders/history. */
const TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "LIMIT", label: "Limit" },
  { value: "MARKET", label: "Market" },
  { value: "CONDITIONAL", label: "Conditional" },
];

/** Terminal statuses accepted by the `status` query param. */
const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "FILLED", label: "Filled" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "REJECTED", label: "Rejected" },
  { value: "EXPIRED", label: "Expired" },
];

const SETTLEMENT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "usdt", label: "USDT" },
  { value: "usdc", label: "USDC" },
  { value: "busd", label: "BUSD" },
];

const CONTRACT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "btcusdt", label: "BTCUSDT" },
  { value: "ethusdt", label: "ETHUSDT" },
  { value: "bnbusdt", label: "BNBUSDT" },
];

const ORDER_DESKTOP_COL_COUNT = 11;

/** API max page size. */
const FETCH_LIMIT = 100;

const FuturesOrderHistory = () => {
  const [filterType, setFilterType] = useState("all");
  const [status, setStatus] = useState("all");
  const [settlement, setSettlement] = useState("all");
  const [contract, setContract] = useState("all");
  const [filterTypeDraft, setFilterTypeDraft] = useState("all");
  const [statusDraft, setStatusDraft] = useState("all");
  const [settlementDraft, setSettlementDraft] = useState("all");
  const [contractDraft, setContractDraft] = useState("all");
  const [rows, setRows] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const loadOrders = useCallback(async () => {
    try {
      const res = await AuthService.futuresOrderHistory({
        symbol: toFuturesApiSymbol(contract),
        status: status !== "all" ? status : undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      const mapped = extractFuturesOrders(res).map(mapFuturesOrderHistoryRow);
      setRows(mapped);
    } catch {
      setRows([]);
    }
  }, [contract, status]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (!matchesFuturesOrderType(row.orderType, filterType)) return false;
      if (!pairMatchesContractFilter(row.rawSymbol || row.contract, contract)) return false;
      if (!settlementMatchesContractCode(row.rawSymbol || row.contract, settlement)) return false;
      return true;
    });
  }, [rows, filterType, contract, settlement]);

  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(filteredRows);

  const resetFilters = () => {
    setFilterType("all");
    setStatus("all");
    setSettlement("all");
    setContract("all");
    setFilterTypeDraft("all");
    setStatusDraft("all");
    setSettlementDraft("all");
    setContractDraft("all");
  };

  const applyMobileFilters = () => {
    setFilterType(filterTypeDraft);
    setStatus(statusDraft);
    setSettlement(settlementDraft);
    setContract(contractDraft);
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const isMob = keyPrefix === "mob";
    const ft = isMob ? filterTypeDraft : filterType;
    const sts = isMob ? statusDraft : status;
    const st = isMob ? settlementDraft : settlement;
    const ct = isMob ? contractDraft : contract;
    const setFt = isMob ? setFilterTypeDraft : setFilterType;
    const setSts = isMob ? setStatusDraft : setStatus;
    const setSt = isMob ? setSettlementDraft : setSettlement;
    const setCt = isMob ? setContractDraft : setContract;
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter order history">
      <div className="so-toolbar__left">
        <CustomDropdown
          id={`${pref}foh-dd-type`}
          label="Type"
          value={ft}
          options={TYPE_OPTIONS}
          onChange={setFt}
          menuKey={`${pref}foh-type`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}foh-dd-status`}
          label="Status"
          value={sts}
          options={STATUS_OPTIONS}
          onChange={setSts}
          menuKey={`${pref}foh-status`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}foh-dd-settlement`}
          label="Settlement Coin"
          value={st}
          options={SETTLEMENT_OPTIONS}
          onChange={setSt}
          menuKey={`${pref}foh-settlement`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}foh-dd-contract`}
          label="Contract"
          value={ct}
          options={CONTRACT_OPTIONS}
          onChange={setCt}
          menuKey={`${pref}foh-contract`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-futures-order-history-title">
            <h1 id="so-futures-order-history-title" className="so-page__title">
              Order History
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/futures_open_orders" },
                { label: "Order History", to: "/user_profile/orders/futures_order_history" },
                { label: "Trade History", to: "/user_profile/orders/futures_trade_history" },
                { label: "Position History", to: "/user_profile/orders/position_history" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap">
              <table className="so-table so-table--rowLines so-table--wide">
                <thead>
                  <tr>
                    <th scope="col">Contract</th>
                    <th scope="col">Creation Time</th>
                    <th scope="col" className="so-table__th--center">
                      Side
                    </th>
                    <th scope="col">Type</th>
                    <th scope="col" className="so-table__th--right">
                      Price
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Fill Price
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Size
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Fee
                    </th>
                    <th scope="col">Remarks</th>
                    {/* <th scope="col">Close Reason</th> */}
                    <th scope="col" className="so-table__th--center">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0
                    ? renderEmptyBody()
                    : pagedRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.contract}</td>
                      <td>{row.creationTime}</td>
                      <td className="so-table__td--center">
                        <span className={row.side === "Buy" ? "so-side--buy" : row.side === "Sell" ? "so-side--sell" : undefined}>
                          {row.side}
                        </span>
                      </td>
                      <td>{row.orderTypeLabel || row.orderType || "—"}</td>
                      <td className="so-table__td--right so-num">
                        <div>{row.price}</div>
                        {row.priceSubtext ? <div className="small text-muted">{row.priceSubtext}</div> : null}
                      </td>
                      <td className="so-table__td--right so-num">{row.fillPrice}</td>
                      <td className="so-table__td--right so-num">{row.size}</td>
                      <td className="so-table__td--right so-num">{row.fee}</td>
                      <td>{row.remarks}</td>
                      {/* <td>{row.closeReason}</td> */}
                      <td className="so-table__td--center">
                        <span className={`so-status ${row.isFilled ? "so-status--completed" : "so-status--pending"}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Order history pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Order History</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Open Orders", to: "/user_profile/orders/futures_open_orders" },
              { label: "Order History", to: "/user_profile/orders/futures_order_history" },
              { label: "Trade History", to: "/user_profile/orders/futures_trade_history" },
              { label: "Position History", to: "/user_profile/orders/position_history" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-foh-filters"
              toolbar={renderToolbar(true, "mob")}
              onSearch={applyMobileFilters}
              onReset={resetFilters}
            />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            pagedRows.map((row) => (
            <OrderMobileCardShell
              key={`m-${row.id}`}
              desktopColumnCount={ORDER_DESKTOP_COL_COUNT}
              popupTitle="Order details"
              heroPrimary={row.contract}
              heroDatetime={row.creationTime}
            >
              <div className="so-mobileCard__row">
                <span>Side</span>
                <span className={row.side === "Buy" ? "so-side--buy" : row.side === "Sell" ? "so-side--sell" : undefined}>
                  {row.side}
                </span>
              </div>
              <div className="so-mobileCard__row">
                <span>Type</span>
                <span>{row.orderTypeLabel || row.orderType || "—"}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Price</span>
                <span className="so-num">{row.price}</span>
              </div>
              {row.priceSubtext ? (
                <div className="so-mobileCard__row">
                  <span>Trigger</span>
                  <span className="so-num">{row.priceSubtext}</span>
                </div>
              ) : null}
              <div className="so-mobileCard__row">
                <span>Fill Price</span>
                <span className="so-num">{row.fillPrice}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Size</span>
                <span className="so-num">{row.size}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Fee</span>
                <span className="so-num">{row.fee}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Remarks</span>
                <span>{row.remarks}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Close Reason</span>
                <span>{row.closeReason}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Status</span>
                <span className={`so-status ${row.isFilled ? "so-status--completed" : "so-status--pending"}`}>
                  {row.status}
                </span>
              </div>
            </OrderMobileCardShell>
          ))
          )}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Order history pages"
          />
        </div>
      </div>
    </div>
  );
};

export default FuturesOrderHistory;
